import { IcoClipboard24 } from '@onefootprint/icons';
import type { GraphOperationPasteNode, GraphOperationResult, PasteNodeData, VaultKind } from '@onefootprint/request-types/dashboard';
import { Tooltip } from '@onefootprint/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEdit from '../../../hooks/use-edit';
import { useGetStaticNodeInfo } from '../../base-node/hooks/use-get-static-node-info';
import { AddNodeButton } from './add-node-button';
type PasteNodeButtonProps = {
  operation: GraphOperationPasteNode;
  onClose: (result?: GraphOperationResult) => void;
  subject: VaultKind;
};
export const PasteNodeButton = ({
  operation,
  onClose,
  subject
}: PasteNodeButtonProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'addNode.pasteNode'
  });
  const mutation = useEdit();
  const {
    speculativeClipboardNodes,
    getNodesToPaste,
    displayPasteButton,
    isSafari,
    safariDisabledReason,
    isBrowserPermissionDenied
  } = useClipboardNodes();
  const getNodeInfo = useGetStaticNodeInfo();
  const handlePasteNode = async () => {
    const nodesToPaste = await getNodesToPaste();
    if (!nodesToPaste) return;
    try {
      const operationData = {
        ...operation,
        nodes: nodesToPaste ?? []
      };
      const operationResult = await mutation.mutateAsync(operationData);
      onClose(operationResult);
    } catch (error) {
      console.error('Error updating playbook session:', error);
    }
  };
  const getDisabledReason = () => {
    if (isSafari) return safariDisabledReason;
    if (!speculativeClipboardNodes) {
      return t($ => $.disabledNothingToPaste);
    }
    const mismatchingSubject = speculativeClipboardNodes?.filter(n => n.subject !== subject);
    if (mismatchingSubject.length > 0) {
      return t($_0 => $_0.disabledSubjectNotMatching, {
        from: mismatchingSubject[0]?.subject,
        to: subject
      });
    }
  };
  const disabledReason = getDisabledReason();
  const getPasteLabel = () => {
    if (!speculativeClipboardNodes) {
      return {
        title: t($_1 => $_1.labelPasteNode),
        description: t($_2 => $_2.descriptionSingle)
      };
    }
    if (speculativeClipboardNodes?.length === 1) {
      const nodeKind = getNodeInfo(speculativeClipboardNodes[0]?.kind).title.lowercase;
      return {
        title: t($_3 => $_3.label, {
          nodeKind
        }),
        description: t($_4 => $_4.descriptionSingle)
      };
    }
    return {
      title: t($_5 => $_5.pasteMultipleNodes),
      description: t($_6 => $_6.descriptionMultiple)
    };
  };
  const {
    title,
    description
  } = getPasteLabel();
  if (isBrowserPermissionDenied) {
    return <AddNodeButton description={t($_7 => $_7.disabledBrowserPermissionDenied)} disabled={true} icon={{
      Icon: IcoClipboard24,
      color: 'primary'
    }} isProcessing={false} onClick={() => {}} title={t($_8 => $_8.labelPasteNode)} />;
  }
  if (!displayPasteButton) {
    return null;
  }
  return <Tooltip disabled={!disabledReason} content={disabledReason} side="left" fullWidth>
      <AddNodeButton description={description} disabled={mutation.isPending || !!disabledReason} icon={{
      Icon: IcoClipboard24,
      color: 'primary'
    }} isProcessing={mutation.isPending} onClick={handlePasteNode} title={title} />
    </Tooltip>;
};
const getIsSafari = (): boolean => typeof window !== 'undefined' && 'safari' in window;
export const useClipboardNodes = () => {
  const [clipboardNodes, setClipboardNodes] = useState<PasteNodeData[] | undefined>(undefined);
  const getNodeInfo = useGetStaticNodeInfo();
  const [isLoaded, setIsLoaded] = useState(false);
  const [safariDisabledReason, setSafariDisabledReason] = useState<string | undefined>(undefined);
  const isSafari = getIsSafari();
  const [isBrowserPermissionDenied, setIsBrowserPermissionDenied] = useState(false);
  const parseClipboardText = async (): Promise<PasteNodeData[] | undefined> => {
    try {
      const text = await navigator.clipboard.readText();
      const nodesJson = JSON.parse(text);
      // biome-ignore lint/suspicious/noExplicitAny: Parsing unknown object
      const isNode = (node: any) => 'kind' in node && getNodeInfo(node.kind).title && 'subject' in node && (node.subject === 'person' || node.subject === 'business');
      if (Array.isArray(nodesJson) && nodesJson.every(isNode)) {
        return nodesJson;
      }
    } catch {
      setSafariDisabledReason('Nothing to paste');
    }
  };
  const loadClipboardText = async () => {
    try {
      // @ts-expect-error - 'clipboard-read' is valid in chrome
      const permissionResult = await navigator.permissions.query({
        name: 'clipboard-read'
      });
      if (permissionResult.state === 'denied') {
        setIsBrowserPermissionDenied(true);
      }
    } catch {}
    if (!isSafari) {
      // Safari has a weird behavior that requires the user to click a "Paste" button in a popup context menu.
      // So for safari, we will lazily load the data from the clipboard
      const nodes = await parseClipboardText();
      if (nodes) {
        setClipboardNodes(nodes);
      }
    }
    setIsLoaded(true);
  };
  useEffect(() => {
    loadClipboardText();
  }, []);
  const getNodesToPaste = async () => {
    let nodesToPaste = clipboardNodes;
    if (isSafari) {
      nodesToPaste = await parseClipboardText();
    }
    return nodesToPaste;
  };

  // Since safari displays an obnoxious prompt to paste from the clipboard, we will always show the
  // paste button in safari, regardless of whether the user has pasteable content on their clipboard.
  // Only after they try to paste will we load the content from their clipboard.
  const displayPasteButton = !!clipboardNodes?.length || !isLoaded || isSafari;
  return {
    /** Upon invoking the paste operation, resolves the set of nodes that should be pasted, regardless of the browser. */
    getNodesToPaste,
    /** Some browsers allow us to peek at what's on the clipboard. This is the list of nodes that will be pasted, only populated for some browsers. DO NOT use this when invoking the paste operation. */
    speculativeClipboardNodes: clipboardNodes,
    displayPasteButton,
    isSafari,
    safariDisabledReason,
    isBrowserPermissionDenied
  };
};